import httpClient from '../httpClient';
import {
  AuthBody,
  EmailOrPhoneRequest,
  LoginRequest,
  LoginResponse,
  PhoneClaimTokenResponse,
  RegisterRequest,
  ResendVerificationMailRequest,
} from '../../models/AuthBody';
import publicHttpClient from 'api/publicHttpClient';
import { User } from 'models/User';

const authPath = (routePath: TemplateStringsArray) => `auth/${routePath}`;

function register(userData: RegisterRequest) {
  return httpClient.post<AuthBody>(authPath`signup`, userData);
}

function login(userData: LoginRequest) {
  return httpClient.post<LoginResponse>(authPath`login`, userData);
}

function validateOtpt(userData: { otpToken: string; userId: string }) {
  return httpClient.post<AuthBody>(authPath`validate-otpt`, userData);
}

function forgotPassword(data: EmailOrPhoneRequest, shareableLinkId?: string) {
  return publicHttpClient.post(authPath`forgot-password`, {
    ...data,
    shareableLinkId,
  });
}

function resetPassword(token: string, password: string) {
  return publicHttpClient.post<AuthBody>(`auth/${token}/reset-password`, {
    password,
  });
}

const refreshToken = (refreshToken: string) => {
  return publicHttpClient.post(`auth/refresh-token`, { refreshToken });
};

function verifyEmail(emailToken: string, leadId?: string) {
  return publicHttpClient.get<AuthBody & { ndaUrl?: string }>(
    `auth/verify-email/${emailToken}`,
    { params: { leadId } },
  );
}

function getPrefilingValues(token: string) {
  return publicHttpClient.get<User>(`auth/external-user/${token}`);
}

function resendVerificationMail(data?: ResendVerificationMailRequest) {
  return httpClient.post<User>('auth/resend-verification-email', data);
}

function unsubscribeMail(token: string) {
  return publicHttpClient.get(`auth/email-unsubscribe/${token}`);
}

function loginWithMagicLink(token: string) {
  return publicHttpClient.get<AuthBody & { redirectPath: string }>(
    `auth/magic-link/${token}`,
  );
}

// Signup-time phone claim. Public because the claimant has no account yet: the
// number must be proven before auth/signup runs, so that dismissing the popup
// leaves nothing behind. `confirm` returns a phoneClaimToken that auth/signup
// then consumes to create the account and move the verification in one
// operation.
function initiatePhoneVerification(contactNumber: string) {
  return publicHttpClient.post(authPath`initiate-phone-verification`, {
    contactNumber,
  });
}

function confirmPhoneVerification(
  contactNumber: string,
  verificationCode: string,
) {
  return publicHttpClient.post<PhoneClaimTokenResponse>(
    authPath`confirm-phone-verification`,
    { contactNumber, verificationCode },
  );
}

export default {
  validateOtpt,
  register,
  login,
  refreshToken,
  verifyEmail,
  forgotPassword,
  resetPassword,
  getPrefilingValues,
  resendVerificationMail,
  unsubscribeMail,
  loginWithMagicLink,
  initiatePhoneVerification,
  confirmPhoneVerification,
};
